import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.iva-store.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Products
export const productService = {
  getAll: (params = {}) => api.get('/products', { params }),
  getAllAdmin: (params = {}) => api.get('/products/admin/all', { params }),
  getById: (id) => api.get(`/products/${id}`),
  search: (query) => api.get(`/products/search?q=${query}`),
  getByCategory: (categoryId) => api.get(`/products/category/${categoryId}`),
  getByColor: (color) => api.get(`/products/color/${color}`),
  getByColorGrouped: () => api.get('/products/colors/grouped'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`)
};

// Categories
export const categoryService = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`)
};

// Users/Auth
export const authService = {
  register: (data) => api.post('/users/register', data),
  completeRegistration: (data) => api.post('/users/complete-registration', data),
  login: (data) => api.post('/users/login', data),
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

// Orders
export const orderService = {
  create: (data) => api.post('/orders', data),
  createGuest: (data) => api.post('/orders/guest', data),
  getMyOrders: () => api.get('/orders/my-orders'),
  getById: (id) => api.get(`/orders/${id}`),
  searchByCodeAndPhone: (orderCode, phone) => api.post('/orders/search', { order_code: orderCode, phone }),
  getAll: (params = {}) => api.get('/orders/admin/all', { params }),
  getAdminById: (id) => api.get(`/orders/admin/${id}`),
  updateDetails: (id, data) => api.put(`/orders/admin/${id}/details`, data),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  updateBulkStatus: (orderIds, status) => api.put('/orders/admin/bulk-status', { order_ids: orderIds, status }),
  updateOrderItems: (id, data) => api.put(`/orders/admin/${id}/items`, data),
  delete: (id) => api.delete(`/orders/admin/${id}`)
};

// Brands (Lens Brands)
export const brandService = {
  // Public: returns array of active brands
  getAllPublic: async () => {
    const res = await api.get('/brands');
    return res?.data?.data ?? res?.data ?? [];
  },
  // Admin: returns array (with counts) for admin
  getAllAdmin: async () => {
    const res = await api.get('/brands/admin/all');
    return res?.data?.data ?? res?.data ?? [];
  },
  getById: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`)
};

// Car Brands
export const carBrandService = {
  getAll: () => api.get('/car-brands'),
  getById: (id) => api.get(`/car-brands/${id}`),
  create: (data) => api.post('/car-brands', data),
  update: (id, data) => api.put(`/car-brands/${id}`, data),
  delete: (id) => api.delete(`/car-brands/${id}`)
};

// Car Models
export const carModelService = {
  getAll: (brandId) => api.get(`/car-models${brandId ? `?brand_id=${brandId}` : ''}`),
  getById: (id) => api.get(`/car-models/${id}`),
  create: (data) => api.post('/car-models', data),
  createWithImage: (formData) => api.post('/car-models', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/car-models/${id}`, data),
  updateWithImage: (id, formData) => api.put(`/car-models/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/car-models/${id}`)
};

// Car Years
export const carYearService = {
  getAll: () => api.get('/car-years'),
  getById: (id) => api.get(`/car-years/${id}`),
  create: (data) => api.post('/car-years', data),
  update: (id, data) => api.put(`/car-years/${id}`, data),
  delete: (id) => api.delete(`/car-years/${id}`)
};

// Suppliers
export const supplierService = {
  getAll: () => api.get('/suppliers'),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`)
};

// Banners (Homepage Slideshow)
export const bannerService = {
  getAll: () => api.get('/banners'),
  getAllAdmin: () => api.get('/banners/admin/all'),
  getById: (id) => api.get(`/banners/admin/${id}`),
  create: (data) => api.post('/banners/admin', data),
  update: (id, data) => api.put(`/banners/admin/${id}`, data),
  delete: (id) => api.delete(`/banners/admin/${id}`),
  reorder: (banners) => api.post('/banners/admin/reorder', { banners }),
  uploadImage: (formData) => api.post('/banners/admin/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// WhatsApp Verification
export const whatsappService = {
  sendOTP: (phone) => api.post('/whatsapp/send-otp', { phone }),
  verifyOTP: (phone, otp) => api.post('/whatsapp/verify-otp', { phone, otp }),
  checkVerification: (phone) => api.get(`/whatsapp/check-verification?phone=${phone}`)
};

// Coupons
export const couponService = {
  validate: (code, orderTotal) => api.post('/coupons/validate', { code, orderTotal }),
  getAll: () => api.get('/coupons'),
  getById: (id) => api.get(`/coupons/${id}`),
  create: (data) => api.post('/coupons', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  toggle: (id) => api.put(`/coupons/${id}/toggle`),
  delete: (id) => api.delete(`/coupons/${id}`)
};

// Reports
export const reportService = {
  getSummary: (params = {}) => api.get('/reports/summary', { params }),
  getLensesBreakdown: (params = {}) => api.get('/reports/lenses-breakdown', { params }),
  getExpenses: (params = {}) => api.get('/reports/expenses', { params }),
  createExpense: (data) => api.post('/reports/expenses', data),
  deleteExpense: (id) => api.delete(`/reports/expenses/${id}`)
};

// Combine all services
api.users = authService;
api.products = productService;
api.orders = orderService;
api.categories = categoryService;
api.brands = brandService;
api.carModels = carModelService;
api.banners = bannerService;
api.whatsapp = whatsappService;
api.coupons = couponService;
api.reports = reportService;

export const exportService = {
  downloadData: () => api.get('/export/data', { responseType: 'blob' }),
  downloadImages: () => api.get('/export/images', { responseType: 'blob', timeout: 0 })
};

export default api;
